import { useEffect, useState } from 'react'
import { executiveApi, type ExecutiveEconomic, type ExecutiveFilters } from '../api/executive'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, List, ListItem, LoadingState, StatCard, StatGrid,
} from './ui'

/**
 * R3-21 - sezione economica del cockpit Direzione (flag `report.cockpit_economico`): ricavi nei tre stati, provvigioni
 * dovute, margine, costi, liquidita', crediti e debiti, cash flow, CAC e ROI, obiettivo, top prodotti e anomalie
 * economiche. Solo con la lettura amministrativa; senza, il pannello non compare.
 */

const eur = (n: number | null | undefined) =>
  n === null || n === undefined ? 'non disponibile' : n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const pct = (n: number | null | undefined) => (n === null || n === undefined ? 'n.d.' : `${(n * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%`)

export function EconomicCockpitPanel({ filters }: { filters: ExecutiveFilters }) {
  const flagOn = useFeatureFlag('report.cockpit_economico')
  const [data, setData] = useState<ExecutiveEconomic | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!flagOn) return
    let alive = true
    setData(null)
    setError('')
    executiveApi.economic(filters)
      .then(d => { if (alive) setData(d) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore') })
    return () => { alive = false }
  }, [flagOn, filters])

  if (!flagOn) return null
  if (error) return <Callout tone="warning">Quadro economico non disponibile. {error}</Callout>
  if (!data) return <LoadingState />
  if (!data.enabled || !data.money || !data.revenue) return null

  const liq = data.liquidity
  const m = data.marketing
  return (
    <>
      <Card padding="none">
        <CardHeader title="Quadro economico" subtitle="Ricavi, margine, liquidità, crediti e debiti" />
        <CardContent>
          <StatGrid>
            <StatCard label="Ricavo presunto" value={eur(data.revenue.presumed)} />
            <StatCard label="Ricavo maturato" value={eur(data.revenue.accrued)} />
            <StatCard label="Ricavo incassato" value={eur(data.revenue.collected)} />
            <StatCard label="Provvigioni dovute" value={eur(data.commissions?.due)} supporting={`Maturate ${eur(data.commissions?.accrued)}, pagate ${eur(data.commissions?.paid)}`} />
            <StatCard label="Margine del mese" value={eur(data.margin?.amount)} supporting={data.margin?.pct != null ? `${data.margin.pct.toLocaleString('it-IT', { maximumFractionDigits: 1 })}%` : undefined} />
            <StatCard label="Costi del mese" value={eur(data.costs)} />
            <StatCard label="Liquidità libera" value={eur(liq?.free)} supporting={liq?.available ? `Banca ${eur(liq.bank)}, cassa ${eur(liq.cash)}` : 'Conti non raggiungibile'} />
            <StatCard label="Crediti aperti" value={eur(data.receivables)} />
            <StatCard label="Debiti aperti" value={eur(data.payables)} />
            <StatCard label="Cash flow 30 giorni" value={eur(data.cashFlow?.net)} supporting={`Entrate ${eur(data.cashFlow?.in30)}, uscite ${eur(data.cashFlow?.out30)}`} />
            <StatCard label="CAC" value={eur(m?.cac)} supporting={`Spesa ${eur(m?.spend)}, attivazioni ${m?.activations ?? 0}`} />
            <StatCard label="ROI marketing" value={pct(m?.roi)} supporting={`Costo per lead ${eur(m?.costPerLead)}`} />
            {data.goal && (
              <StatCard label="Obiettivo attivazioni" value={`${data.goal.pct.toLocaleString('it-IT')}%`} supporting={`${data.goal.actual} su ${data.goal.target.toLocaleString('it-IT')}`} />
            )}
          </StatGrid>
        </CardContent>
      </Card>

      {(data.anomalies?.length ?? 0) > 0 && (
        <Card padding="none">
          <CardHeader title="Anomalie economiche" />
          <List aria-label="Anomalie economiche">
            {data.anomalies!.map(a => (
              <ListItem key={`${a.kind}|${a.key}`} headline={<>{a.severity === 'alta' ? 'Alta' : 'Media'}: {a.title}</>} />
            ))}
          </List>
        </Card>
      )}

      <Card padding="none">
        <CardHeader title="Prodotti migliori" subtitle="Per margine nel periodo" />
        <CardContent>
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Prodotto</th>
                <th className="text-end">Pratiche</th>
                <th className="text-end">Ricavo</th>
                <th className="text-end">Margine</th>
              </tr>
            </thead>
            <tbody>
              {(data.topProducts ?? []).map(p => (
                <tr key={p.key}>
                  <td>{p.label}</td>
                  <td className="text-end md-num">{p.practices}</td>
                  <td className="text-end md-num">{eur(p.revenue)}</td>
                  <td className="text-end md-num">{eur(p.margin)}</td>
                </tr>
              ))}
              {(data.topProducts ?? []).length === 0 && <DataTableEmptyRow colSpan={4} title="Nessun prodotto nel periodo." />}
            </tbody>
          </DataTable>
        </CardContent>
      </Card>

      {data.limitations && (
        <ul className="md-typescale-body-small text-muted mb-0">
          {data.limitations.map(l => <li key={l}>{l}</li>)}
        </ul>
      )}
    </>
  )
}
